/**
 * Department-to-Default-Contact Mapping
 *
 * Defines the default "Notify Others" external recipient e-mail address for
 * each department. When a user opens a time-off or overtime request form, the
 * `notify_others` field is pre-populated with the contact corresponding to
 * their department so that the right stakeholder is always notified by default.
 *
 * Department keys are stored in **lowercase** in the database (normalised by
 * the employee-sync worker) so all keys in this map are lowercase.
 *
 * Users may remove the pre-populated recipient or add additional ones at any
 * time before submitting.
 *
 * @module departmentDefaults
 */

import type { NotifyRecipient } from './dataProvider';

/**
 * Maps a normalised (lowercase) department name to the default external
 * notification e-mail address for that department.
 *
 * Keys match the values stored in the `employees.department` column after
 * the sync worker trims and lowercases the Azure AD value.
 *
 * @example
 * // Look up the default contact for a user in the HR department
 * const email = DEPARTMENT_DEFAULT_CONTACT['hr']; // 'hien.nguyen@devsoft.vn'
 */
export const DEPARTMENT_DEFAULT_CONTACT: Readonly<Record<string, string>> = {
    hr: 'hien.nguyen@devsoft.vn',
    nexus: 'michele@superseats.com',
    song: 'jennifer@superseats.com',
    dlvry: 'Ethan.Chin@dlvry.com',
    anesis: 'paul.song@anesispain.com',
} as const;

/**
 * Returns the default {@link NotifyRecipient} for a given department, or
 * `null` when the department has no configured default.
 *
 * The returned recipient is treated as an external contact (i.e.
 * `employee_id` is `null`), which means it will be serialised as a plain
 * e-mail address rather than an internal employee identifier when the form
 * is submitted.
 *
 * The `department` value is normalised (trimmed + lowercased) before lookup
 * so callers do not need to normalise it themselves.
 *
 * @param department - The department name from the employee record (e.g. `'hr'` or `'HR'`).
 * @returns A `NotifyRecipient` with `employee_id: null` and the default e-mail,
 *   or `null` if no mapping exists for the given department.
 *
 * @example
 * const recipient = getDefaultNotifyRecipient('nexus');
 * // { employee_id: null, full_name: 'michele@superseats.com', email: 'michele@superseats.com' }
 */
export function getDefaultNotifyRecipient (department: string | null | undefined): NotifyRecipient | null {
    if (!department) return null;
    const normalised = department.trim().toLowerCase();
    const email = DEPARTMENT_DEFAULT_CONTACT[normalised];
    if (!email) return null;
    return { employee_id: null, full_name: email, email };
}
